import {
  Component,
  OnInit,
  Inject,
  OnDestroy,
  HostListener,
} from '@angular/core';
import {
  ActivatedRoute,
  Router,
  RouterLink,
  RoutesRecognized,
} from '@angular/router';
import { RouterOutlet } from '@angular/router';
import {
  MsalService,
  MsalModule,
  MsalBroadcastService,
  MSAL_GUARD_CONFIG,
  MsalGuardConfiguration,
} from '@azure/msal-angular';
import {
  InteractionStatus,
  RedirectRequest,
  EventMessage,
  EventType,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter, pairwise, takeUntil } from 'rxjs/operators';
import { CommonService } from './services/common.service';
import { AppModule } from './utilities/app/app.module';
import { LoadingIndicatorComponent } from './components/loading-indicator/loading-indicator.component';
import { MenuItem, MessageService } from 'primeng/api';
import { ThemeService } from './services/theme.service';
import { CookieService } from 'ngx-cookie-service';
import { LoginComponent } from './components/login/login.component';
import { Location } from '@angular/common';
import { environment } from '../environments/environment';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  providers: [MessageService],
  imports: [
    MsalModule,
    RouterOutlet,
    RouterLink,
    AppModule,
    LoadingIndicatorComponent,
    LoginComponent,
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  isHome = false;

  @HostListener('window:resize', ['$event.target.innerWidth'])
  onResize(width: number) {
    if (width > 575) this.overlayVisible = false;
  }

  overlayVisible = false;

  title = 'Pixel Probe';
  isIframe = false;
  loginDisplay = false;
  loggedin_profile: any;
  logout_items: any;
  activeItem: any;

  public loading = false;
  private readonly _destroying$ = new Subject<void>();

  navigation_items = [
    // {
    //   label: 'Landing',
    //   routerLink: ['/landing']
    // },
    {
      label: 'Home',
      routerLink: ['/home'],
    },
    {
      label: 'Video Library',
      routerLink: ['/video_library'],
    },
  ];

  previous_route = new BehaviorSubject<string>('home');

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    public commonservice: CommonService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private themeService: ThemeService,
    private cookieService: CookieService,
    private location: Location,
    private messageService: MessageService
  ) {
    this.router.events.subscribe((router_event: any) => {
      if (
        router_event.url?.includes('home') ||
        router_event.url?.includes('login') ||
        router_event.url?.includes('explore')
      ) {
        this.isHome = true;
      } else {
        this.isHome = false;
        this.router.events
          .pipe(
            filter((e: any) => e instanceof RoutesRecognized),
            pairwise()
          )
          .subscribe((e: any) => {
            this.previous_route.next(e[0].urlAfterRedirects.replace('/', ''));
          });
      }
    });
  }

  ngOnInit(): void {
    this.themeService.setTheme(this.cookieService.get('preferred_theme'));
    this.authService.handleRedirectObservable().subscribe();
    this.isIframe = window !== window.parent && !window.opener; // Remove this line to use Angular Universal
    this.setLoginDisplay();

    this.authService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACCOUNT_ADDED ||
            msg.eventType === EventType.ACCOUNT_REMOVED
        )
      )
      .subscribe((result: EventMessage) => {
        if (this.authService.instance.getAllAccounts().length === 0) {
          window.location.pathname = '/';
          this.loading = false;
        } else {
          this.setLoginDisplay();
          this.commonservice.getUserDetails();
          this.commonservice.getUser_image();
          this.activeItem = this.navigation_items[0];
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();
        this.commonservice.getUserDetails();
        this.commonservice.getUser_image();
        this.router.navigate(['/home']);
        // this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Welcome to app Login success' });
      });

    let silent_request = {
      scopes: [...environment.apiConfig.scopes],
      account: this.authService.instance.getAllAccounts()[0],
      forceRefresh: true
    };

    this.authService.acquireTokenSilent(silent_request).subscribe({
      next: (res: any) => {
        localStorage.setItem('access_token', res.idToken);
      },
      error: (err: any) => {
        if (err instanceof InteractionRequiredAuthError) {
          // fallback to interaction when silent call fails
          return this.authService.acquireTokenPopup(silent_request);
        }
        return;
      },
    });

    /*this.msalBroadcastService.msalSubject$.subscribe({
      next: (msalSubject) => {
        if (msalSubject.eventType === 'msal:acquireTokenFailure') {
          localStorage.clear();
        }
      },
    });*/

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE
        )
      )
      .subscribe({
        next: () => {
          localStorage.clear();
        },
      });

    this.commonservice.logged_in_user_details.subscribe((user_profile: any) => {
      if (user_profile && JSON.stringify(user_profile) !== '{}') {
        this.loggedin_profile = user_profile;
        this.logout_items = [];
        this.logout_items.push({
          label: 'Logout',
          icon: 'pi pi-fw pi-sign-out',
          command: () => {
            this.logout();
          },
        });
      }
    });

    this.commonservice.message_broker.subscribe((message: any) => {
      this.messageService.add(message);
    });

    this.messageService.messageObserver.subscribe((message: any) => {
      if (message) {
        console.log('message', message);
      }
    });
  }

  async setLoginDisplay() {
    if (this.authService.instance.getAllAccounts().length > 0) {
      this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
    } else {
      await this.loginRedirect();
    }
  }

  checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    let activeAccount = this.authService.instance.getActiveAccount();

    if (
      !activeAccount &&
      this.authService.instance.getAllAccounts().length > 0
    ) {
      let accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
    }
    activeAccount = this.authService.instance.getActiveAccount();
    if (activeAccount?.idToken) {
      localStorage.setItem('access_token', activeAccount.idToken);
    }
  }

  loginRedirect() {
    if (this.msalGuardConfig.authRequest) {
      this.authService.loginRedirect({
        ...this.msalGuardConfig.authRequest,
      } as RedirectRequest);
    } else {
      this.authService.loginRedirect();
    }
  }

  logout() {
    this.authService.logoutRedirect();
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  onActiveItemChange(event: MenuItem) {
    this.activeItem = event;
  }
  get_icon_class(): string {
    if (this.themeService.theme === 'light') {
      return 'pi pi-moon';
    }
    return 'pi pi-sun';
  }

  switchTheme(): void {
    this.themeService.switchTheme();
  }

  handleImageError(event: any) {
    const imageElement = event.target;
    imageElement.style.opacity = '0'; // Hide the actual image on error
    imageElement.nextElementSibling.style.opacity = '1'; // Show fallback content
  }

  navigate_back() {
    this.location.back();
  }
}
